import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, FileSignature } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Card, Pill } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";

const EMPTY = { id: null, name: "", description: "", body: "", active: true };

function Editor({ t, vars, onSaved }) {
  const [f, setF] = useState(t);
  const ref = useRef(null);
  const insert = (k) => {
    const el = ref.current; const pos = el?.selectionStart ?? f.body.length; const tag = `{{${k}}}`;
    setF({ ...f, body: f.body.slice(0, pos) + tag + f.body.slice(pos) });
  };
  const save = async () => {
    try { const body = { name: f.name, description: f.description, body: f.body, active: f.active };
      const { data } = f.id ? await api.put(`/admin/contract-templates/${f.id}`, body) : await api.post("/admin/contract-templates", body);
      toast.success("Modelo salvo"); onSaved(data.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-5 space-y-4" testid="template-editor">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Nome do modelo</Label><Input className="mt-1.5" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="template-name-input" /></div>
        <div><Label>Descrição</Label><Input className="mt-1.5" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="template-description-input" /></div>
      </div>
      <div><Label>Variáveis (clique para inserir no texto)</Label>
        <div className="flex flex-wrap gap-1.5 mt-1.5">{vars.map((v) => <button key={v.key} type="button" title={v.label} onClick={() => insert(v.key)} data-testid={`template-var-${v.key}`} className="font-mono text-[11px] px-2 py-1 rounded-md border bg-slate-50 hover:bg-indigo-50 hover:border-indigo-300">{`{{${v.key}}}`}</button>)}</div></div>
      <div><Label>Texto do contrato</Label><Textarea ref={ref} className="mt-1.5 min-h-[380px] text-xs leading-relaxed" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="template-body-input" />
        <p className="text-[11px] text-slate-500 mt-1">Separe as cláusulas com uma linha em branco. Modelo de referência: revise com o jurídico antes do uso.</p></div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="template-active-switch" />Ativo para uso</label>
        <Button onClick={save} disabled={!f.name.trim() || !f.body.trim()} data-testid="template-save-button">Salvar modelo</Button>
      </div>
    </Card>
  );
}

export default function AdminContractTemplates() {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = (id) => api.get("/admin/contract-templates").then((r) => { setData(r.data); setSel(r.data.templates.find((t) => t.id === id) || r.data.templates[0] || EMPTY); });
  useEffect(() => { load(); }, []);
  if (!data) return null;
  return (
    <div data-testid="admin-contract-templates">
      <PageHeader eyebrow="Administração" title="Modelos de contrato" subtitle="Textos usados para gerar contratos de admissão com assinatura digital. As variáveis são preenchidas com os dados do funcionário e da empresa."
        actions={<Button onClick={() => setSel(EMPTY)} data-testid="template-new-button"><Plus className="h-4 w-4 mr-1.5" />Novo modelo</Button>} />
      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <div className="space-y-2">{data.templates.map((t) => (
          <button key={t.id} onClick={() => setSel(t)} data-testid={`template-item-${t.id}`} className={`w-full text-left border rounded-xl p-3 bg-white hover:border-indigo-300 ${sel?.id === t.id ? "border-indigo-500 ring-2 ring-indigo-100" : ""}`}>
            <div className="flex items-center gap-2 text-sm font-medium"><FileSignature className="h-4 w-4 text-indigo-600 shrink-0" />{t.name}</div>
            <div className="text-[11px] text-slate-500 mt-1">{t.description}</div>
            {!t.active && <div className="mt-1.5"><Pill cls="bg-slate-100 text-slate-600 border-slate-200">Inativo</Pill></div>}
          </button>))}</div>
        {sel && <Editor key={sel.id || "new"} t={sel} vars={data.variables} onSaved={load} />}
      </div>
    </div>
  );
}
